import {
  accountSearchColumns,
  hanThanhToanSearchColumns,
  lyDoNhapXuatSearchColumns,
  MA_CHUNG_TU,
  QUERY_KEYS,
  supplierSearchColumns
} from '@/constants';
import { AritoIcon, CurrencyInput, DocumentNumberField, FormField, SearchField } from '@/components/custom/erp';
import { AccountModel, HanThanhToan, KhachHang, NhapXuat } from '@/types/schemas';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface BasicInfoTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

export default function BasicInfoTab({ formMode, formState: { state, actions } }: BasicInfoTabProps) {
  return (
    <div className='flex flex-col gap-3 p-4'>
      {/* Customer Information Section */}
      <div className='grid grid-cols-12 gap-6'>
        {/* Left Column */}
        <div className='col-span-6 space-y-1'>
          <div className='flex items-center'>
            <Label className='w-40 font-medium'>Mã nhà cung cấp</Label>
            <SearchField<KhachHang>
              type='text'
              name='ma_kh'
              value={state.nhaCungCap?.customer_code || ''}
              searchEndpoint={`/${QUERY_KEYS.NHA_CUNG_CAP}/`}
              searchColumns={supplierSearchColumns}
              onRowSelection={actions.setNhaCungCap}
              columnDisplay='customer_code'
              dialogTitle='Danh mục nhà cung cấp'
              className='w-[205px]'
              disabled={formMode === 'view'}
            />

            <div className='ml-4 flex items-center'>
              <Label className='w-20 font-medium'>Mã số thuế</Label>
              <FormField
                name='ma_so_thue'
                type='text'
                disabled={formMode === 'view'}
                placeholder='Nhập và tra cứu'
                value={state.nhaCungCap?.tax_code || ''}
              />
              <button
                type='button'
                className='ml-2 flex items-center justify-center border border-gray-200 px-3 py-1.5'
              >
                <AritoIcon icon={15} className='shrink-0' />
              </button>
              <button
                type='button'
                className='ml-2 flex items-center justify-center border border-gray-200 px-3 py-1.5'
              >
                <AritoIcon icon={888} />
              </button>
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-40 font-medium'>Tên nhà cung cấp</Label>
            <div className='flex-1'>
              <FormField
                name='ten_kh_thue'
                type='text'
                disabled={formMode === 'view'}
                value={state.nhaCungCap?.customer_name || ''}
                placeholder='Nhập tên nhà cung cấp/đơn vị'
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-40 font-medium'>Địa chỉ</Label>
            <div className='flex-1'>
              <FormField
                name='dia_chi'
                type='text'
                disabled={formMode === 'view'}
                labelClassName='w-48 font-medium'
                value={state.nhaCungCap?.address || ''}
                placeholder='Nhập địa chỉ nhà cung cấp'
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-40 font-medium'>Tài khoản nợ</Label>
            <SearchField<AccountModel>
              type='text'
              name='tk'
              value={state.taiKhoan?.code || state.nhaCungCap?.account_data?.code || ''}
              relatedFieldValue={state.nhaCungCap?.account_data?.name || state.taiKhoan?.name || ''}
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
              columnDisplay='code'
              displayRelatedField='name'
              searchColumns={accountSearchColumns}
              dialogTitle='Danh sách tài khoản'
              onRowSelection={actions.setTaiKhoan}
              disabled={formMode === 'view'}
              className='w-100'
            />
          </div>

          <div className='flex items-center'>
            <div className='flex-1'>
              <FormField
                label='Diễn giải'
                name='dien_giai'
                type='text'
                disabled={formMode === 'view'}
                labelClassName='w-40 font-medium'
                value={state.dienGiai || ''}
                onValueChange={e => actions.setDienGiai(e)}
              />
            </div>
          </div>
          <div className='flex items-center'>
            <div className='flex-1'>
              <FormField
                label='Nhóm hàng hóa'
                name='ten_vt'
                type='text'
                disabled={formMode === 'view'}
                labelClassName='w-40 font-medium'
              />
            </div>
          </div>
          <div className='flex items-center'>
            <div className='flex-1'>
              <FormField
                label='Ghi chú'
                name='ghi_chu'
                type='text'
                disabled={formMode === 'view'}
                labelClassName='w-40 font-medium'
              />
            </div>
          </div>
        </div>

        {/* Middle Column */}
        <div className='col-span-3 space-y-1'>
          <div className='ml-4 flex h-8 items-center'></div>

          <div className='ml-4 flex items-center'>
            <FormField
              name='ong_ba'
              label='Người nhận'
              labelClassName='w-32 font-medium'
              inputClassName='w-6 text-red-600'
              disabled={formMode === 'view'}
              className='w-[250px]'
              value={state.nhaCungCap?.contact_person || ''}
              placeholder='Nhập tên người nhận'
            />
          </div>

          <div className='ml-4 flex items-center'>
            <FormField
              name='e_mail'
              label='Email'
              labelClassName='w-32 font-medium'
              inputClassName='w-64'
              className='w-[250px]'
              disabled={formMode === 'view'}
              value={state.nhaCungCap?.email || ''}
              placeholder='Nhập email'
            />
          </div>

          <div className='ml-4 flex items-center'>
            <div className='flex items-center'>
              <Label className='w-[132px] font-medium'>Hạn thanh toán</Label>
              <SearchField<HanThanhToan>
                type='text'
                name='ma_tt'
                value={state.hanThanhToan?.ma_tt || state.nhaCungCap?.payment_term_data?.ma_tt || ''}
                searchEndpoint={`/${QUERY_KEYS.HAN_THANH_TOAN}/`}
                columnDisplay='ma_tt'
                searchColumns={hanThanhToanSearchColumns}
                dialogTitle='Danh mục hạn thanh toán'
                className='w-[230px]'
                onRowSelection={actions.setHanThanhToan}
                placeholder='Hạn thanh toán'
                disabled={formMode === 'view'}
              />
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className='col-span-3 flex'>
          {/* Tab Content */}
          <div className='pr-2'>
            <DocumentNumberField
              ma_ct={MA_CHUNG_TU.MUA_HANG.HOA_DON_DICH_VU_TRA_LAI_NHA_CUNG_CAP}
              quyenChungTu={state.quyenChungTu}
              onQuyenChungTuChange={actions.setQuyenChungTu}
              soChungTu={state.soChungTu}
              onSoChungTuChange={actions.setSoChungTu}
              classNameSearchField='flex-1'
              labelClassName='w-40 font-medium'
              formMode={formMode}
            />

            <div className='flex items-center'>
              <Label className='w-40 font-medium'>Ngày chứng từ</Label>
              <FormField name='ngay_ct' type='date' disabled={formMode === 'view'} className='w-72' />
            </div>
            <div className='flex items-center'>
              <Label className='w-40 font-medium'>Số hóa đơn đầu vào</Label>
              <FormField name='so_ct0' disabled={formMode === 'view'} placeholder='SỐ HÓA ĐƠN' className='w-72' />
            </div>
            <div className='flex items-center'>
              <Label className='w-40 font-medium'>Ký hiệu</Label>
              <FormField name='so_ct2' disabled={formMode === 'view'} placeholder='KÝ HIỆU' className='w-72' />
            </div>
            <div className='flex items-center'>
              <Label className='w-40 font-medium'>Ngày hoá đơn</Label>
              <FormField name='ngay_ct0' type='date' className='w-72' disabled={formMode === 'view'} />
            </div>

            <CurrencyInput formMode={formMode} labelClassName='w-40 font-medium' />

            <div className='flex items-center'>
              <Label className='w-40 font-medium'>Trạng thái</Label>
              <div className='flex-1'>
                <FormField
                  name='status'
                  type='select'
                  disabled={formMode === 'view'}
                  options={[
                    { value: '0', label: 'Chưa ghi sổ' },
                    { value: '3', label: 'Chờ duyêt' },
                    { value: '5', label: 'Đã ghi sổ' },
                    { value: '9', label: 'Huỷ' }
                  ]}
                />
              </div>
            </div>

            <div className='mt-1 flex'>
              <div className='mb-4 h-2 w-36 shrink-0' />
              <FormField
                label='Dữ liệu được nhận'
                name='transfer_yn'
                type='checkbox'
                disabled={true}
                labelClassName='w-32'
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
